/**
 * ThriveImportFlow — the Thrive path of the New Import wizard (epic
 * 2026-10-15-thrive-search-import, sub-task 03): 1 Source · 2 Thrive search ·
 * 3 Stages · 4 Link to Search, one step at a time, with Back and Next; the last
 * step carries the Import button (the Preview step was dropped on Jared's
 * feedback, 2026-10-20). Next and Import stay disabled, with the reason beside
 * them, until the step has what they need. State lives in `useThriveImport`.
 *
 * Link to Search is the upload path's `LinkSearchStep` with its own label (the
 * link stays in NU Assist; nothing goes to Thrive): the TARGET search (Open +
 * BD, required). When it is the source search too, a note under it says so; it
 * does not block.
 *
 * Tokenized: token color utilities + `text-*` roles. No inline styles.
 */

import { Button } from "@nu-assist/ui";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { ImportsClient, SearchStatusFilter } from "../../../clients/imports.client";
import { LinkSearchStep } from "./steps/LinkSearchStep";
import type { SearchTypeaheadResult } from "./steps/SearchTypeahead";
import { StagesStep, candidacyCountLabel } from "./steps/StagesStep";
import { ThriveSearchStep } from "./steps/ThriveSearchStep";
import { THRIVE_STEPS, type ThriveStep, countsAreExact, useThriveImport } from "./useThriveImport";

const STEP_LABELS: Record<ThriveStep, string> = {
  search: "2 Thrive search",
  stages: "3 Stages",
  target: "4 Link to Search",
};

/** The target field's label on the Thrive path (Jared, 2026-10-20). */
export const THRIVE_TARGET_LABEL =
  "Search that you want to link this list to here in NU Assist (it won’t go to Thrive)";

export interface ThriveImportFlowProps {
  client: ImportsClient;
  /** The source picker's fetch: any status. */
  searchSource: (query: string, status?: SearchStatusFilter) => Promise<SearchTypeaheadResult>;
  /** The target picker's fetch: Open + BD, as on upload. */
  searchTarget: (query: string) => Promise<SearchTypeaheadResult>;
  /** The Source cards, rendered under the rail. */
  sourceStep: ReactNode;
}

export function ThriveImportFlow({
  client,
  searchSource,
  searchTarget,
  sourceStep,
}: ThriveImportFlowProps) {
  const navigate = useNavigate();
  const flow = useThriveImport(client);
  const current = THRIVE_STEPS.indexOf(flow.step);
  const exact = flow.preview ? countsAreExact(flow.preview) : true;
  const importLabel = flow.submitting
    ? "Importing…"
    : `Import ${candidacyCountLabel(flow.checkedCount, exact)} candidacies`;

  return (
    <>
      <ol className="mb-8 flex flex-wrap gap-2" data-testid="step-rail">
        {["1 Source", ...THRIVE_STEPS.map((step) => STEP_LABELS[step])].map((label, i) => {
          // Step 1 (Source) is always on screen; the rail's current step is
          // the one under it.
          const isCurrent = i === current + 1;
          return (
            <li
              key={label}
              aria-current={isCurrent ? "step" : undefined}
              className={`rounded-md px-3 py-1.5 text-caption ${
                isCurrent ? "bg-accent-muted text-accent" : "bg-surface text-text-secondary"
              }`}
            >
              {label}
            </li>
          );
        })}
      </ol>

      <div className="flex max-w-3xl flex-col gap-8">
        {sourceStep}

        {flow.step === "search" ? (
          <section aria-label="Thrive search">
            <ThriveSearchStep
              value={flow.source}
              onChange={flow.chooseSource}
              search={searchSource}
              title={flow.title}
              onTitleChange={flow.setTitle}
            />
          </section>
        ) : null}

        {flow.step === "stages" && flow.source ? (
          <section aria-label="Stages">
            <StagesStep
              sourceId={flow.source.id}
              preview={flow.preview}
              loading={flow.previewLoading}
              failure={flow.previewFailure}
              onRetry={flow.retryPreview}
              isChecked={flow.isChecked}
              onToggle={flow.toggleStage}
              checkedStages={flow.checkedStages}
              checkedCount={flow.checkedCount}
            />
          </section>
        ) : null}

        {flow.step === "target" ? (
          <section aria-label="Link to Search" className="flex flex-col gap-2">
            <LinkSearchStep
              label={THRIVE_TARGET_LABEL}
              value={flow.target?.id ?? null}
              valueName={flow.target?.name ?? null}
              onChange={(id, name) => flow.setTarget(id ? { id, name: name ?? "" } : null)}
              search={searchTarget}
            />
            {flow.target && flow.source && flow.target.id === flow.source.id ? (
              <p data-testid="thrive-same-search-note" className="text-caption text-text-secondary">
                This is also the search you are pulling from: Add to search will find everyone
                already there.
              </p>
            ) : null}
          </section>
        ) : null}

        {flow.submitError ? (
          <p data-testid="thrive-import-error" role="alert" className="text-caption text-error">
            {flow.submitError}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          {current > 0 ? (
            <Button
              variant="secondary"
              onClick={flow.goBack}
              disabled={flow.submitting}
              data-testid="wizard-back"
            >
              Back
            </Button>
          ) : null}
          {flow.step === "target" ? (
            <Button
              onClick={flow.submit}
              disabled={flow.submitting || flow.blockedReason !== null}
              data-testid="thrive-import-submit"
            >
              {importLabel}
            </Button>
          ) : (
            <Button
              onClick={flow.goNext}
              disabled={flow.blockedReason !== null}
              data-testid="wizard-next"
            >
              Next
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => navigate("/research/imports")}
            disabled={flow.submitting}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          {flow.blockedReason ? (
            <p data-testid="wizard-next-reason" className="text-caption text-text-secondary">
              {flow.blockedReason}
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
}
